import qs from "qs";
import { toast } from "sonner";
import { getAccessTokenProvider } from "@/auth/access-token-provider";
import { GLOBAL_CONFIG } from "@/global-config";
import { getAcceptLanguage, t } from "@/locales/i18n";
import { useOAuthError, useWrapperResult } from "./hooks";
import {
  authenticateResponseInterceptor,
  errorMessageResponseInterceptor,
  isSessionExpiredError,
  RequestClient,
  type RequestClientOptions,
  RequestError,
} from "./request-client";

function createRequestClient(baseURL: string, options?: RequestClientOptions) {
  const client = new RequestClient({
    ...options,
    baseURL,
  });

  return client;
}

export const requestClient = createRequestClient(GLOBAL_CONFIG.apiBaseUrl, {
  responseFormat: "body",
  paramsSerializer: (params: Record<string, unknown>) => qs.stringify(params, { arrayFormat: "repeat" }),
});

let interceptorsInstalled = false;

/**
 * 装配请求层拦截器：鉴权头 → 响应解包 → 401 静默刷新 → 统一错误提示。
 * 由 `src/main.tsx` 在选定 `AccessTokenProvider` 之后调用一次；重复调用（HMR）会被忽略，
 * 否则拦截器会叠加，一次错误弹多条 toast。
 *
 * 这里不再直接依赖 OIDC：Token 一律向 `AccessTokenProvider` 取，
 * 独立端和嵌入端各自实现，共享其后的请求、准入和授权链路
 * （docs/embedded-workbench-design.md §9）。
 */
export function initRequestClient() {
  if (interceptorsInstalled) {
    return;
  }
  interceptorsInstalled = true;

  /**
   * 认证终局由当前 Provider 处理：standalone 提示用户选择是否重新登录，
   * embedded 结束嵌入会话并通知宿主，绝不在 iframe 里做顶层导航（AR-27）。
   */
  async function doReAuthenticate() {
    await getAccessTokenProvider().onAuthenticationFailed("unauthorized");
  }

  /**
   * 刷新 token 逻辑。并发 401 由认证拦截器的共享 Promise 合并成一次，
   * 嵌入端 Provider 也会合并向宿主索取 Token 的请求，避免刷新风暴（§6.5）。
   */
  async function doRefreshToken() {
    const token = await getAccessTokenProvider().requestAccessToken("unauthorized");
    if (!token) {
      throw new Error("未能获取新的 Access Token。");
    }
    return token;
  }

  function formatToken(token: null | string) {
    return token ? `Bearer ${token}` : null;
  }
  // 请求头处理
  requestClient.addRequestInterceptor({
    fulfilled: async (config) => {
      const tokenProvider = getAccessTokenProvider();
      if (tokenProvider.isSessionExpired?.()) {
        throw new RequestError(401, t("auth.sessionExpired.detail"), undefined, null, null, true);
      }
      // Authorization
      const accessToken = await tokenProvider.getAccessToken();
      if (accessToken) {
        config.headers.Authorization = formatToken(accessToken);
      }
      // Read on every request so changing the workbench language immediately changes
      // ABP exception, validation, and other localized server responses as well.
      config.headers["Accept-Language"] = getAcceptLanguage();
      return config;
    },
  });

  // response数据解构
  requestClient.addResponseInterceptor<any>({
    fulfilled: (response) => {
      const { config, data, status } = response;
      if (config.responseFormat === "raw") return response;

      // biome-ignore lint/correctness/useHookAtTopLevel: <必须在顶层使用hook>
      const { hasWrapResult, getData } = useWrapperResult(response);

      if (hasWrapResult()) {
        return getData();
      }

      if (status >= 200 && status < 400) {
        return data;
      }

      throw Object.assign({}, response, { response });
    },
  });

  // token过期的处理
  requestClient.addResponseInterceptor(
    authenticateResponseInterceptor({
      client: requestClient,
      onSessionExpired: doReAuthenticate,
      isSessionExpired: () => getAccessTokenProvider().isSessionExpired?.() ?? false,
      doRefreshToken,
      enableRefreshToken: true,
      formatToken,
    }),
  );

  // 通用的错误处理,如果没有进入上面的错误处理逻辑，就会进入这里
  requestClient.addResponseInterceptor(
    errorMessageResponseInterceptor((msg: string, error) => {
      // 调用方已自行展示错误（如列表缺省图、内联提示）时跳过 toast，避免重复。
      if (isSessionExpiredError(error) || error?.config?.skipErrorToast) {
        return;
      }
      // 这里可以根据业务进行定制,你可以拿到 error 内的信息进行定制化处理，根据不同的 code 做不同的提示，而不是直接使用 message.error 提示 msg
      // 兼容 OAuth 与 ABP 后端返回的不同错误结构。
      const responseData = error?.response?.data ?? {};
      if (responseData?.error_description) {
        // biome-ignore lint/correctness/useHookAtTopLevel: <必须在顶层使用hook>
        const { formatError } = useOAuthError();
        toast.error(formatError(responseData) || msg);
        return;
      }
      // ABP's message is the localized, user-facing value. Details may contain
      // diagnostics or a stack trace and must not override it in a toast.
      const errorMessage = responseData?.error?.message ?? responseData?.error?.details ?? "";
      // 如果没有错误信息，则会根据状态码进行提示
      toast.error(errorMessage || msg);
    }),
  );
}

export * from "./hooks";
export * from "./request-client";
